// src/services/reactionLive.js — my reaction, shown on every copy of a post
// before the server has counted it.
//
// The feed, a profile, the post pane and the reels pane each hold their own copy
// of a post, refreshed by their own Firestore listener. A press is remembered
// here (services/reactions pendingFor) and every surface draws its copy through
// `useLiveReactions`, so the count, the kinds on the right and my face all move
// at once, everywhere. The press stops applying the moment the copy in hand
// shows the server has moved (withPending), and is forgotten after a while in
// any case — a listener that never fires cannot pin a stale number.
import { useEffect, useState } from 'react';
import { pendingFor, withPending } from './reactions';

const PEND_MS = 15000;
const pending = new Map();      // postId → { pend, at }
const listeners = new Set();

function fire() { listeners.forEach((fn) => { try { fn(); } catch { /* a listener's own problem */ } }); }

/** Remember a press on `post` by `uid`: from `prev` (null = none) to `next` (null = taken back). */
export function pressReaction(post, uid, prev, next) {
  if (!post || !post.id || !uid) return;
  /* 🟥 A SECOND PRESS BEFORE THE SERVER HAS COUNTED THE FIRST is measured from
     what the SERVER has, not from the first press: like then unlike is no
     change at all, not one down. The copy in hand still shows the server's
     counters while the first press is pending, so its `prev` is the one kept. */
  const cur = pending.get(String(post.id));
  const from = cur && withPending(post, cur.pend) !== post ? cur.pend.prev : prev;
  pending.set(String(post.id), { pend: pendingFor(post, uid, from, next), at: Date.now() });
  fire();
  setTimeout(() => {
    const cur = pending.get(String(post.id));
    if (cur && Date.now() - cur.at >= PEND_MS) { pending.delete(String(post.id)); fire(); }
  }, PEND_MS + 50);
}

/** Forget a press (the server refused it). */
export function dropReaction(postId) {
  if (pending.delete(String(postId))) fire();
}

/** A post with my unconfirmed press applied — the plain function. */
export function liveReactions(post) {
  if (!post || !post.id) return post;
  const cur = pending.get(String(post.id));
  return cur ? withPending(post, cur.pend) : post;
}

/** …and the hook every surface that draws a post's counts uses. */
export function useLiveReactions(post) {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => (n + 1) % 1e9);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return liveReactions(post);
}
